import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ListTable, { ListTableActions, ListTableColumn } from '../../shared/listView/ListTable';
import { preferencesPhrasesStorage } from '../../services/preferencesPhrasesStorage';
import Checkbox from '../Checkbox';
import PhrasesListItemNumberField from './PhrasesListItemNumberField';
import PhrasesListItemWelcomeIcon from './PhrasesListItemWelcomeIcon';
import { createValuesString } from './util';
import useWindowDimensions from '../../hooks/useWindowDimension';
import PreferencesListSelectBlock from './PreferencesListSelectBlock';
import PreferencesListSelectIsDefault from './PreferencesListSelectIsDefault';
import DeletePhraseIcon from './DeletePhraseIcon';
import DeletePhrasePopup from './DeletePhrasePopup';

type PhraseRow = {
    id: string;
    content: string;
    vote?: number;
    isWelcome?: boolean;
};

const PhraseCheckbox = ({ phraseId }: { phraseId: string }) => {
    const [isChecked, setIsChecked] = useState(
        () => preferencesPhrasesStorage.getSelectedList().some((it) => it.id === phraseId),
    );

    useEffect(() => {
        const sync = () => {
            setIsChecked(preferencesPhrasesStorage.getSelectedList().some((it) => it.id === phraseId));
        };
        preferencesPhrasesStorage.registerFiltersChangingObserver(sync);
        return () => preferencesPhrasesStorage.unRegisterFiltersChangingObserver(sync);
    }, [phraseId]);

    return (
        <Checkbox
            checked={isChecked}
            handleChecked={() => preferencesPhrasesStorage.changeSelectedPhrasesList(phraseId)}
        />
    );
};

const PhrasesTable = () => {
    const { i18n } = useTranslation();
    const { width } = useWindowDimensions();
    const [items, setItems] = useState<PhraseRow[]>(() => [...preferencesPhrasesStorage.getPhrases()]);

    const refresh = () => setItems([...preferencesPhrasesStorage.getPhrases()]);

    useEffect(() => {
        preferencesPhrasesStorage.registerFiltersChangingObserver(refresh);
        refresh();
        return () => preferencesPhrasesStorage.unRegisterFiltersChangingObserver(refresh);
    }, []);

    const columns = useMemo<ListTableColumn<PhraseRow>[]>(() => [
        {
            id: 'check',
            header: '',
            className: 'list-table__td_check',
            cell: (row) => (
                <ListTableActions>
                    <PhraseCheckbox phraseId={row.id} />
                </ListTableActions>
            ),
        },
        {
            id: 'content',
            header: i18n.t('app.page.preference.phrase'),
            cell: (row) => (
                <span className="list-table__phrase" title={row.content}>{row.content}</span>
            ),
        },
        {
            id: 'meta',
            header: i18n.t('app.page.listView.type'),
            cell: (row) => (
                <span className="list-table__muted">{createValuesString(row, i18n)}</span>
            ),
        },
        {
            id: 'welcome',
            header: '',
            className: 'list-table__td_icon',
            cell: (row) => (
                <ListTableActions>
                    <PhrasesListItemWelcomeIcon preferencePhraseId={row.id} />
                </ListTableActions>
            ),
        },
        {
            id: 'vote',
            header: '',
            className: 'list-table__td_vote',
            cell: (row) => (
                <ListTableActions>
                    <PhrasesListItemNumberField
                        preferencePhraseId={row.id}
                        ratingValue={row.vote}
                        phrasesListManager={preferencesPhrasesStorage}
                    />
                </ListTableActions>
            ),
        },
    ], [i18n]);

    return (
        <>
            <DeletePhrasePopup notifyPhrasesList={(phraseId) => {
                preferencesPhrasesStorage.removePhrase(phraseId);
                refresh();
            }} />
            <ListTable
                columns={columns}
                rows={items}
                rowKey={(row) => row.id}
                width={width}
                isRowSelected={(row) => preferencesPhrasesStorage.getSelectedList().some((it) => it.id === row.id)}
            />
            {preferencesPhrasesStorage.getSelectedList().length ? (
                <PreferencesListSelectBlock itemsManager={preferencesPhrasesStorage}>
                    <PreferencesListSelectIsDefault phrasesListManager={preferencesPhrasesStorage} />
                    <DeletePhraseIcon />
                </PreferencesListSelectBlock>
            ) : null}
        </>
    );
};

export default PhrasesTable;
